import { SubmitHandler, useForm } from 'react-hook-form';

import {
  Centered,
  Cta,
  Divider,
  GoogleOauth,
  Layout,
} from 'apps/auth/scripts/components';
import { Group, Input } from 'apps/shared/scripts/components';
import { useToast, useYupValidation } from 'apps/shared/scripts/hooks';

import { FORM_FIELDS } from './constants';
import FormSubheader from './FormSubheader';
import messages from './messages.json';
import { FormValues } from './types';
import { loginSchema } from './validation';

export interface Props {
  onboarding?: boolean;
  handleOAuthLogin: (email: string) => Promise<void>;
  handleLogin: (data: FormValues) => Promise<void>;
  isLoading: boolean;
  modal?: boolean;
  onForgot?: () => void;
  onSignup?: () => void;
}

const LoginForm = ({
  onboarding,
  handleOAuthLogin,
  handleLogin,
  isLoading,
  modal,
  onForgot,
  onSignup,
}: Props) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: useYupValidation<FormValues>(loginSchema),
  });
  const toast = useToast();

  const onLogin: SubmitHandler<FormValues> = async (data) => {
    try {
      await handleLogin(data);
    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
      }
    }
  };

  const onOAuthLogin = async (email: string) => {
    try {
      await handleOAuthLogin(email);
    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
      }
    }
  };

  return (
    <Layout
      centered={!modal && !onboarding}
      buttonLabel={messages.buttonLabel}
      heading={onboarding ? messages.headingAlt : messages.heading}
      isLoading={isLoading}
      subheader={
        !modal && !onboarding ? (
          <Centered>
            <FormSubheader />
          </Centered>
        ) : (
          <FormSubheader onClick={onSignup} />
        )
      }
      footer={
        <Cta
          label={messages.footerLabel}
          onClick={onForgot}
          to={'/forgot'}
          footer
        />
      }
      onSubmit={handleSubmit(onLogin)}
      modal={modal}
      onboarding={onboarding}
    >
      <GoogleOauth onOAuthLogin={onOAuthLogin} />
      <Divider />
      <Group
        errors={errors}
        label={FORM_FIELDS.EMAIL.label}
        validate={FORM_FIELDS.EMAIL.name}
      >
        <Input control={control} name={FORM_FIELDS.EMAIL.name} type="email" />
      </Group>
      <Group
        errors={errors}
        label={FORM_FIELDS.PASSWORD.label}
        validate={FORM_FIELDS.PASSWORD.name}
        spacing
      >
        <Input
          control={control}
          name={FORM_FIELDS.PASSWORD.name}
          type="password"
        />
      </Group>
    </Layout>
  );
};

LoginForm.displayName = 'LoginForm';

export default LoginForm;
